import { Dispatch, MutableRefObject, SetStateAction } from "react";
import { ButtonTypes, SelectFieldOption_Values } from "../../../Definitions";
import { FeedComponentInfo } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { FeedCurve, FeedCurveComponent } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { MaintainFeedCurve } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/maintainFeedCurveCall";
import { colors, glyphs } from "../../../Styles";
import { GenericRow } from "../../Tables/_Tables/useColumnConstructor";
import { cloneDeep, partition } from "lodash";
import NewButton from "../../BaseComponents/newButton";

const REMOVE_ROW_ACTION_COLUMN_STATES = {
  hidden: "hidden",
  visible: "visible"
} as const

type RemoveRowActionColumnStates = typeof REMOVE_ROW_ACTION_COLUMN_STATES[keyof typeof REMOVE_ROW_ACTION_COLUMN_STATES];


export const SHOULD_DISPLAY_CONTINUES = false;

export interface FeedCurveTableData extends GenericRow {
  milestone?: SelectFieldOption_Values,
  milestoneId: number,
  id: number,
  dsWeight: number,
  totalAmount: number,
  nonNormalizedTotalAmount: number,
  day: number,
  addDay: JSX.Element,
  dayIndex: number,
  milestoneFirstDay: number;
  milestoneIndex: number;
  milestoneArrayIndex: number;
  removeRowActionColumn: RemoveRowActionColumnStates;
}

export const convertFeedCurveToTableData = (feedCurve: FeedCurve, setFeedCurve: Dispatch<SetStateAction<FeedCurve>>, createDayIdRef: MutableRefObject<number>, createMilestoneIdRef: MutableRefObject<number>, shouldContinuesDays: boolean) => {
  let firstDay = 0;
  let lastDayTracker = 0;
  let feedDayTracker = 0;
  const data = new Map<number, FeedCurveTableData>();
  const milestones = feedCurve.milestones;
  const components = feedCurve.components;
  if (milestones && milestones.length) {
    milestones.sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex)).forEach((milestone, milestoneArrayIndex) => {
      lastDayTracker = milestoneArrayIndex === 0 ? feedDayTracker : feedDayTracker+1;
      if (milestoneArrayIndex !== 0 && shouldContinuesDays) {
        firstDay = (lastDayTracker);
      }
      const feedDays = milestone.feedDays?.length ? milestone.feedDays : [{day: 0, dayId: -createDayIdRef.current, dayComponents: []}]
      feedDays.sort((dayA, dayB) => (dayA.day-dayB.day)).forEach((feedDay, dayIndex, array) => {
        if ((milestoneArrayIndex === 0 && dayIndex === 0)) {
          firstDay = feedDay.day;
        }
        feedDayTracker = shouldContinuesDays ? (feedDay.day + lastDayTracker) : feedDay.day;
        const dayTotal = feedDay.dayComponents.reduce((accumulator, component) => {if (feedDay.day === 0){} return accumulator += component.amount},0)
        let dsWeight = 0;
        const dayComponents = (components ?? []).map((component) => {
          const dayComponent = feedDay.dayComponents.find((dayComponent) => dayComponent.componentId === component.componentId);
          if (dayComponent) {
            dsWeight += dayComponent.amount*component.ds;
            return {[String(component.componentId)]:  dayComponent.amount}
          }
          return {[String(component.componentId)]: undefined}
        })
        data.set( feedDay.dayId, {
          addDay: 
            <NewButton
              onClick={() => { // make function with state and side effect that does not relay on feedCurve
                array.push({day: (milestone.feedDays.at(-1)?.day??0)+1, dayId: -createDayIdRef.current, dayComponents: []})
                createDayIdRef.current += 1;
                setFeedCurve(cloneDeep(feedCurve));
              }}
              startEnhancer={glyphs.Add({fillColor: colors.greyDarkSecondary, size: "10px"})}
              content=""
              buttonType={ButtonTypes.textButton}
              overrides={{startEnhancerStyles: {margin: "0px"}, contentStyles: {color: colors.greyDarkPrimary}, baseStyles: {visibility: dayIndex !== (array.length - 1)  ? "hidden" : "visible"}}}
            />,
            // <Button
            // onMouseUp={() => { // make function with state and side effect that does not relay on feedCurve
            //   array.push({day: (milestone.feedDays.at(-1)?.day??0)+1, dayId: -createDayIdRef.current, dayComponents: []})
            //   createDayIdRef.current += 1;
            //   setFeedCurve(cloneDeep(feedCurve));
            // }}
            // startEnhancer={glyphs.Add({fillColor: colors.greyDarkSecondary, size: "10px"})}
            // content=""
            // buttonType={ButtonTypes.textButton}
            // overrides={{startEnhancerStyles: {margin: "0px"}, contentStyles: {color: colors.greyDarkPrimary}, baseButton: {visibility: dayIndex !== (array.length - 1)  ? "hidden" : "visible"}}}
            // />,
          milestoneIndex: milestone.milestoneIndex,
          milestoneArrayIndex,
          milestoneFirstDay: shouldContinuesDays ? firstDay : 0,
          milestone: dayIndex === 0 ? {label: milestone.name, id: milestone.milestoneId} : undefined,
          milestoneId: milestone.feedCurveMilestoneId, // WARNING changing this to milestone id and not feedCurveMilestoneId requires updating how this row entry is handled in the table;
          id: feedDay.dayId,
          day: feedDayTracker,
          dayIndex,
          dsWeight,
          ds: dayTotal === 0 ? 0 : Math.round((dsWeight/dayTotal)*100)/100,
          totalAmount: Math.round((dayTotal/1000)*100)/100,
          removeRowActionColumn: (dayIndex===0 && (milestone.feedDays?.length > 1 || milestones.length === 1)) ? "hidden" : "visible",
          nonNormalizedTotalAmount: dayTotal,
          ...Object.assign({}, ...dayComponents)
        })
      } )
    });
  }
  if (milestones?.length === 0) {
    feedCurve.milestones = [{milestoneId: 2, feedCurveMilestoneId: -createMilestoneIdRef.current, name: "Farrowing", milestoneIndex: 1, feedDays: [{day: 0, dayId: -createDayIdRef.current, dayComponents: []}]}]
    if (components === undefined) {
      feedCurve.components = [];
    }
    createDayIdRef.current += 1;
    createMilestoneIdRef.current += 1;
    setFeedCurve(cloneDeep(feedCurve))
  }
  return data;
}

export const handleEnergyEdit =(data: Map<number, FeedCurveTableData>, feedCurveComponents: FeedCurveComponent[] | undefined, isEnergyEdit: Boolean) =>{
  const dataCopy = new Map(data);

  if (!feedCurveComponents) {
    return dataCopy
  }
  
  dataCopy.forEach((row, key) => {
    const dayComponents = feedCurveComponents.map((component) => {
      const rowValue = row[component.componentId]
      if (rowValue !== undefined) {
        return {[String(component.componentId)]:Math.round((+rowValue / 1000 * component.energy)*100)/100};
      }
      return {[String(component.componentId)]: undefined}
    })
    dataCopy.set(key, {...row, ...Object.assign({}, ...dayComponents)})
  })
  return dataCopy;
}

export const handlePercentileEdit = (data: Map<number, FeedCurveTableData>, feedCurveComponents: number[] | undefined, isPercentileEdit: Boolean) => {
  const dataCopy = new Map(data);
  if (!feedCurveComponents) {
    return dataCopy
  }
  dataCopy.forEach((row, key) => {
    const dayComponents = feedCurveComponents.map((component) => {
      const rowValue = row[component]
      if (rowValue !== undefined) {
        return {
          [String(component)]: isPercentileEdit ? Math.round((+rowValue/row.nonNormalizedTotalAmount)*100): Math.round((+rowValue/1000)*100)/100 
        }
      }
      return {[String(component)]: undefined}
    })
    dataCopy.set(key, {...row, ...Object.assign({}, ...dayComponents)})
  })
  return dataCopy;
}

export function valueIsSelectFieldOptionsValue(value: string | number | boolean | SelectFieldOption_Values | undefined, accessor: string): value is SelectFieldOption_Values {
  return accessor === "milestone" && value !== undefined;
}

/**
 * @deprecated WARNING the value is still a string (And I can't remember why i did this an type guarded it as number and not string)
 */
export function valueIsNumberValue(value: string | number | boolean | SelectFieldOption_Values | undefined, accessor: string): value is number {
  return accessor !== "milestone" && value !== undefined && value !== "";
}

export const handleNewFeedComponent = ({ feedComponentId, feedCurve, feedComponents, dirtRef }: { feedComponentId?: number; feedCurve: FeedCurve; feedComponents?: FeedComponentInfo[]; dirtRef: React.MutableRefObject<{[key: number]: MaintainFeedCurve} & {"baseFeedCurve": MaintainFeedCurve}>; }) => {
  if (feedComponentId === undefined || feedComponents === undefined) {
    return feedCurve;
  }
  const feedComponent = feedComponents.find((feedComponent) => feedComponent.id === feedComponentId);
  if (feedComponent) {
    if (feedCurve.components === undefined) {
      feedCurve.components = []
    }
    feedCurve.components.push({limit: 0, gramPerMinute: 0, componentId: feedComponent.id, name: feedComponent.name, ds: feedComponent.ds, feedCurveComponentId: -feedComponent.id, energy: feedComponent.energy })
  
  if (dirtRef.current.baseFeedCurve.creates === undefined) {
    dirtRef.current.baseFeedCurve.creates = {};
  }
  if (dirtRef.current.baseFeedCurve.creates!.components === undefined) {
    dirtRef.current.baseFeedCurve.creates!.components = [];
  }
  dirtRef.current.baseFeedCurve.creates!.components!.push({component_id: feedComponent?.id, temporaryId: feedComponent?.id, gram_per_minute: 0})
  }
  return feedCurve;
}

export const handleRemoveComponent = ({ componentId, feedCurveComponentId, componentIndex, feedCurve, dirtRef }: { componentId: number; feedCurveComponentId: number; componentIndex: number; feedCurve: FeedCurve; dirtRef: MutableRefObject<{[key: number]: MaintainFeedCurve} & {"baseFeedCurve": MaintainFeedCurve} >; }): FeedCurve=> {
  feedCurve.components?.splice(componentIndex, 1);
  if (feedCurveComponentId < 0) {
    if (dirtRef.current.baseFeedCurve.creates?.components) {
      const feedCurveComponentIndex = dirtRef.current.baseFeedCurve.creates.components.findIndex((component) => component.temporaryId === feedCurveComponentId);
      dirtRef.current.baseFeedCurve.creates.components.splice(feedCurveComponentIndex, 1);
    }
  } else {
    if (dirtRef.current.baseFeedCurve.deletes === undefined) {
      dirtRef.current.baseFeedCurve.deletes = {};
    }
    if (dirtRef.current.baseFeedCurve.deletes.components === undefined) {
      dirtRef.current.baseFeedCurve.deletes.components = [];
    }
    dirtRef.current.baseFeedCurve.deletes.components.push(feedCurveComponentId);
  }
  feedCurve.milestones?.forEach((milestone) => {
    milestone.feedDays.forEach((feedDay) => {
    const [daysToKeep, daysToRemove]  = partition(feedDay.dayComponents, (dayComponent) => !(dayComponent.componentId===componentId))
    feedDay.dayComponents = daysToKeep;
    daysToRemove.forEach((dayComponent) => {
      if (dayComponent.dayComponentId < 0) {
        //remove from creates
        dirtRef.current[milestone.feedCurveMilestoneId].creates?.milestones?.forEach((dirtyMilestone) => {
          dirtyMilestone.feedDays?.forEach((dirtyFeedDay) => {
            dirtyFeedDay.dayComponents = dirtyFeedDay.dayComponents?.filter((dayComponent) => (!(dayComponent.component_id === componentId)));
          })
        })
      } else {
        if ( dirtRef.current[milestone.feedCurveMilestoneId] === undefined) {
          dirtRef.current[milestone.feedCurveMilestoneId] = {feedCurveId: feedCurve.feedCurveId}
        }
        if (dirtRef.current[milestone.feedCurveMilestoneId].deletes === undefined) {
          dirtRef.current[milestone.feedCurveMilestoneId].deletes = {}
        }
        if (dirtRef.current[milestone.feedCurveMilestoneId].deletes!.dayComponents === undefined) {
          dirtRef.current[milestone.feedCurveMilestoneId].deletes!.dayComponents = []
        }

        dirtRef.current[milestone.feedCurveMilestoneId].deletes!.dayComponents!.push(dayComponent.dayComponentId);
      } 

    })
  })
  })
  return cloneDeep(feedCurve);
};

const interpolate = (x1: number, y1: number, x2: number, y2: number, x: number): number => {
  if (x2 === x1) return y1; 
  const a = (y2 - y1) / (x2 - x1);
  const b = y1 - a * x1;
  return a * x + b; 
};

export const handleInterpolatedFeedRow = (
  feedCurveComponents: FeedCurveComponent[] | undefined,
  infimum: FeedCurveTableData,
  supremum: FeedCurveTableData,
  selectedDay: number
): FeedCurveTableData | null => {
  
  if (!feedCurveComponents || !infimum || !supremum) {
    return null;
  }

  // Lineær interpolation i gram
  // Interpoler hver komponent individuelt
  const interpolatedComponents = feedCurveComponents.map(component => {
    const compId = String(component.componentId);
    const y1 = Number(infimum[compId]) || 0;
    const y2 = Number(supremum[compId]) || 0;
    const interpolatedValue = interpolate(infimum.day, y1, supremum.day, y2, selectedDay);
    return { [compId]: parseFloat(interpolatedValue.toFixed(2)) }; 
  });

  // Beregn total i gram
  const nonNormalizedTotalAmount = interpolatedComponents.reduce((sum, obj) => {
    const value = Object.values(obj)[0] ?? 0;
    return sum + Number(value);
  }, 0);

  // Beregn dsWeight
  const dsWeight = feedCurveComponents.reduce((sum, component) => {
    const compId = String(component.componentId);
    const val = interpolatedComponents.find(obj => obj[compId] !== undefined)?.[compId] ?? 0;
    return sum + val * component.ds;
  }, 0);
  
  // Konstruér ny række
  const newRow: FeedCurveTableData = {
    id: -Math.abs(selectedDay),
    day: selectedDay,
    dayIndex: infimum.dayIndex + 0.5,
    dsWeight: parseFloat(dsWeight.toFixed(2)),
    ds: nonNormalizedTotalAmount === 0 ? 0 : parseFloat((dsWeight / nonNormalizedTotalAmount).toFixed(2)),
    totalAmount: parseFloat(nonNormalizedTotalAmount.toFixed(2)),
    nonNormalizedTotalAmount: parseFloat(nonNormalizedTotalAmount.toFixed(2)),
    removeRowActionColumn: "visible",
    addDay: <></>,
    ...Object.assign({}, ...interpolatedComponents),
    milestoneId: infimum.milestoneId,
    milestoneArrayIndex: infimum.milestoneArrayIndex,
  };

  return newRow;
};

